import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ButtonLink } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { AnchorLink, BodyText, EditorialHeader, Overline, PageSection, SectionTitle, TextLink } from '@components/common/Editorial';
import { usePageTitle } from '@hooks/usePageTitle';
const primaryServices = ['support', 'diagnostics', 'security'];
const supportingServices = ['tools', 'automation', 'audience'];
const steps = ['listen', 'review', 'resolve'];
export const ServicesPage = () => {
    const { t } = useTranslation();
    usePageTitle('services.title');
    return (_jsxs(_Fragment, { children: [_jsxs(EditorialHeader, { eyebrow: t('services.title'), title: t('interior.services.title'), intro: t('interior.services.intro'), children: [_jsxs(ButtonLink, { to: "/contact", children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] }), _jsxs(AnchorLink, { href: "#service-options", children: [t('interior.services.explore'), _jsx(ArrowDown, { size: 17, "aria-hidden": "true" })] })] }), _jsx(PageSection, { id: "service-options", "aria-labelledby": "service-options-title", children: _jsxs(Container, { children: [_jsx(Overline, { id: "service-options-title", children: t('interior.services.primary') }), _jsx(ServiceList, { children: primaryServices.map((key, index) => (_jsxs(ServiceRow, { children: [_jsxs(ServiceHeading, { children: [_jsxs("span", { "aria-hidden": "true", children: ["0", index + 1] }), _jsx(SectionTitle, { children: t(`services.items.${key}`) })] }), _jsxs(ServiceCopy, { children: [_jsx("p", { children: t(`services.descriptions.${key}`) }), _jsx(Examples, { children: t(`interior.services.examples.${key}`) }), _jsxs(TextLink, { to: "/contact", children: [t('interior.services.ask'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] })] }, key))) })] }) }), _jsx(MethodSection, { "aria-labelledby": "service-method-title", children: _jsxs(MethodGrid, { children: [_jsxs("div", { children: [_jsx(Overline, { children: t('interior.services.methodEyebrow') }), _jsx(SectionTitle, { id: "service-method-title", children: t('interior.services.methodTitle') }), _jsx(BodyText, { children: t('home.remote.text') }), _jsx(RemoteLimit, { children: t('home.remote.note') }), _jsxs(TextLink, { to: "/contact", children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] }), _jsx(MethodSteps, { children: steps.map((key, index) => (_jsxs("li", { children: [_jsxs("span", { "aria-hidden": "true", children: ["0", index + 1] }), _jsxs("div", { children: [_jsx("h3", { children: t(`home.process.steps.${key}.title`) }), _jsx("p", { children: t(`home.process.steps.${key}.text`) })] })] }, key))) })] }) }), _jsx(PageSection, { "aria-labelledby": "service-more-title", children: _jsxs(Container, { children: [_jsx(SectionTitle, { id: "service-more-title", children: t('interior.services.moreTitle') }), _jsx(SupportingList, { children: supportingServices.map((key) => (_jsxs(SupportingRow, { children: [_jsx("h3", { children: t(`services.items.${key}`) }), _jsx("p", { children: t(`services.descriptions.${key}`) }), _jsxs(TextLink, { to: key === 'tools' ? '/products' : '/contact', children: [key === 'tools' ? t('actions.viewProducts') : t('actions.contact'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] }, key))) }), _jsxs(ClosingRow, { children: [_jsxs("div", { children: [_jsx(SectionTitle, { children: t('interior.services.closingTitle') }), _jsx(BodyText, { children: t('interior.services.closingText') })] }), _jsxs(ButtonLink, { to: "/contact", children: [t('actions.requestAssistance'), _jsx(ArrowUpRight, { size: 18, "aria-hidden": "true" })] })] })] }) })] }));
};
const ServiceList = styled.div `
  border-top: 1px solid ${({ theme }) => theme.colors.navy900};
`;
const ServiceRow = styled.article `
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 4rem;
  padding-block: 2.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.25rem;
    padding-block: 2rem;
  }
`;
const ServiceHeading = styled.div `
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr);
  gap: 1rem;
  > span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
    padding-top: 0.5rem;
  }
  ${SectionTitle} {
    font-size: clamp(1.6rem, 2.3vw, 2.15rem);
  }
`;
const ServiceCopy = styled.div `
  > p:first-child {
    margin: 0;
    max-width: 58ch;
    color: ${({ theme }) => theme.colors.ink};
    font-size: 1.125rem;
    line-height: 1.7;
  }
  ${TextLink} {
    margin-top: 1rem;
  }
`;
const Examples = styled.p `
  margin: 0.8rem 0 0;
  max-width: 58ch;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1rem;
  line-height: 1.75;
`;
const MethodSection = styled(PageSection) `
  background: ${({ theme }) => theme.colors.surface};
`;
const MethodGrid = styled(Container) `
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 5rem;
  @media (max-width: 899px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 2.5rem;
  }
`;
const RemoteLimit = styled.p `
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.9375rem;
  line-height: 1.7;
`;
const MethodSteps = styled.ol `
  list-style: none;
  margin: 0;
  padding: 0 0 0 1rem;
  border-left: 2px solid ${({ theme }) => theme.colors.mint200};
  display: grid;
  align-content: start;
  gap: 2rem;
  li {
    display: grid;
    grid-template-columns: 2rem minmax(0, 1fr);
    gap: 1rem;
  }
  li > span {
    color: ${({ theme }) => theme.colors.blue600};
    font-size: 0.75rem;
    padding-top: 0.3rem;
  }
  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.125rem;
    line-height: 1.4;
  }
  p {
    margin: 0.6rem 0 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.7;
  }
`;
const SupportingList = styled.div `
  margin-top: 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
`;
const SupportingRow = styled.article `
  display: grid;
  grid-template-columns: minmax(0, 0.65fr) minmax(0, 1fr) auto;
  gap: 2rem;
  align-items: baseline;
  padding-block: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.125rem;
    line-height: 1.5;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1rem;
    line-height: 1.7;
  }
  @media (max-width: 899px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.6rem;
  }
`;
const ClosingRow = styled.div `
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 2rem;
  margin-top: 4rem;
  padding-top: 2.5rem;
  border-top: 2px solid ${({ theme }) => theme.colors.navy900};
  ${SectionTitle} {
    font-size: clamp(1.6rem, 2.3vw, 2rem);
  }
  ${BodyText} {
    max-width: 48ch;
    font-size: 1rem;
  }
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
    > a {
      justify-self: start;
    }
  }
`;
